import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getBalanceData, saveBalanceData, type BalanceEntry } from "./api";
import { LEVEL_FIELDS, validateBalanceData, type BalanceData } from "./balance-schema";
import { useDirtyStore } from "./dirty-store";

export const STAT_COLUMNS = [
  ["baseValue", "Base value"], ["softCap", "Soft cap"], ["hardCap", "Hard cap"],
  ["minSocketValue", "Min socket"], ["maxSocketValue", "Max socket"],
] as const;
const key = ["balance-data"];

// Owns the local draft and server operations; the page only renders and edits cells.
export function useBalanceEditor() {
  const client = useQueryClient();
  const query = useQuery({ queryKey: key, queryFn: getBalanceData, refetchOnWindowFocus: false });
  const [base, setBase] = useState<BalanceEntry | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const setDirty = useDirtyStore(s => s.setDirty);
  useEffect(() => { if (!base && query.data) setBase(query.data); }, [base, query.data]);
  const draft = useMemo(() => {
    if (!base) return null;
    const next: BalanceData = structuredClone(base.data);
    const read = (id: string, previous: number) => id in edits ? (edits[id].trim() === "" ? NaN : Number(edits[id])) : previous;
    LEVEL_FIELDS.forEach(field => { next[field] = next[field].map((value, i) => read(`${field}:${i}`, value)); });
    next.statData.forEach((row, i) => STAT_COLUMNS.forEach(([field]) => { row[field] = read(`${field}:${i}`, row[field]); }));
    return next;
  }, [base, edits]);
  const issues = draft ? validateBalanceData(draft) : [];
  const dirty = !!base && JSON.stringify(draft) !== JSON.stringify(base.data);
  useEffect(() => { setDirty(dirty); return () => setDirty(false); }, [dirty, setDirty]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const save = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error("Balance data has not loaded.");
      const result = await saveBalanceData(draft);
      return { revision: result.revision, updatedAt: Date.now(), data: draft };
    },
    onSuccess: entry => { setBase(entry); setEdits({}); client.setQueryData(key, entry); },
  });
  const edit = (id: string, value: string) => { setEdits(current => ({ ...current, [id]: value })); save.reset(); };
  const cellValue = (id: string, original: number) => edits[id] ?? String(original);
  const reset = () => { setEdits({}); save.reset(); };
  return { query, base, issues, dirty, save, edit, cellValue, reset };
}
